/************************************************************************************************************
Author                          : Trizent Technologies Private Limited
Date                            : 
Notes/Remarks                   : 

Organization:  	Trizent Technologies Private Limited.
		Module No. 17, SBC, Thejaswini Building
		Phase 1, Technopark, Thiruvananthapuram - 695581, 
		Kerala, India
 
Copyright 2026 (C) Trizent Technologies Pvt Ltd. (www.trizentinc.com)
All rights reserved.
***************************************************************************************************************/

import { Column } from "../../../../common/commonComponents/mediaPreparationTable";
import {
	MBReconJustification,
	ReconMediaBatchDetails,
	ReconMediaBatchResource,
} from "../reconcileMediaBatch.types";
import MBDeviationInput from "./MBDeviationInput";

export const getMediaBatchReconciliationColumns = (t: any): Column[] => [
	{
		header: t("mediaReconcile.mediaBatch.columns.batchId"),
		key: "batch_identifier",
		render: (row: ReconMediaBatchDetails) => (
			<td className="px-6 py-4 whitespace-nowrap">
				<span className="font-black text-slate-800 text-sm">
					{row.media_batch_identifier}
				</span>
			</td>
		),
	},
	{
		header: t("mediaReconcile.mediaBatch.columns.mediaType"),
		key: "media_name",
		render: (row: ReconMediaBatchDetails) => (
			<td className="px-6 py-4 whitespace-nowrap">
				<span className="text-xs font-bold text-slate-600">
					{row.media_name}
				</span>
			</td>
		),
	},
	{
		header: t("mediaReconcile.mediaBatch.columns.operator"),
		key: "operator_name",
		render: (row: ReconMediaBatchDetails) => (
			<td className="px-6 py-4 whitespace-nowrap">
				<span className="text-xs text-slate-500">
					{row.verified_by_name}
				</span>
			</td>
		),
	},
	{
		header: t("mediaReconcile.mediaBatch.columns.date"),
		key: "verified_at",
		render: (row: ReconMediaBatchDetails) => (
			<td className="px-6 py-4 whitespace-nowrap">
				<span className="text-xs font-mono text-slate-400">
					{row.verified_at}
				</span>
			</td>
		),
	},
	{
	  header: t("mediaReconcile.tableColumns.status"),
	  key: "batch_status",
	  render: (row: ReconMediaBatchDetails) => (
	    <td className="px-6 py-4 whitespace-nowrap">
				<span
					className={`px-2.5 py-1 rounded-full text-[9px] font-black uppercase border text-green-600 bg-green-50 border-green-200`}
				>
					{row.batch_status}
				</span>
	    </td>
	  ),
	},
	{
		header: t("mediaReconcile.tableColumns.reconciliationStatus"),
		key: "reconciliation_status",
		render: (row: ReconMediaBatchDetails) => (
			<td className="px-6 py-4 whitespace-nowrap">
				<span
					className={`px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-widest border ${
						row.reconciliation_status === "PENDING"
							? "text-blue-700 bg-blue-50 border-blue-100"
						: row.reconciliation_status === "OVERDUE"	
							? "text-red-700 bg-red-50 border-red-100"
							: 'text-green-700 bg-green-50 border-green-100'
					}`}
				>
					{row.reconciliation_status}
				</span>
			</td>
		),
	},
];

const hasDeviation = (row: ReconMediaBatchResource) => {
	return Number(row.actual_quantity) !== Number(row.planned_quantity);
};

export const getMediaBatchReconDetailCols = (
	t: any,
	handleReasonChange: (
		ref: string,
		reason: string,
		resource_type: string,
	) => void,
	justifications: Record<string, MBReconJustification>,
) => [
	{
		header: t("mediaReconcile.mediaBatch.columns.resource"),
		key: "lot_reference",
		ClassNames: "text-center font-bold text-slate-700 whitespace-nowrap",
		render: (row: ReconMediaBatchResource) => (
			<>
				{row.resource_name}
				<br />
				({row.lot_reference})
			</>
		),
	},
	{
		header: t("mediaReconcile.mediaBatch.columns.batchPlan"),
		key: "planned_quantity",
		className: "text-center font-black text-slate-400 whitespace-nowrap",
		render: (row: ReconMediaBatchResource) => (
			<>
				{row.planned_quantity}
				{row.unit}
			</>
		),
	},
	{
		header: t("mediaReconcile.mediaBatch.columns.actualUsed"),
		key: "actual_quantity",
		className: "text-center font-black text-slate-400 whitespace-nowrap",
		render: (row: ReconMediaBatchResource) => (
			<>
				{row.actual_quantity}
				{row.unit}
			</>
		),
	},
	{
		header: t("mediaReconcile.mediaBatch.columns.impact"),
		key: "impact",
		className: "text-center whitespace-nowrap",
		render: (row: ReconMediaBatchResource) => (
			<span className="text-[10px] font-black uppercase text-[#FF7043] bg-orange-50 px-2 py-0.5 rounded">
				{row.impact}
			</span>
		),
	},
		{
			header: t("mediaReconcile.mediaBatch.columns.reasonForDeviation"),
			key: "reason",
			render: (row: ReconMediaBatchResource) => (
				<MBDeviationInput
					row={row}
					justifications={justifications}
					hasDeviation={hasDeviation}
					handleReasonChange={handleReasonChange}
				/>
			),
		}
];
